// Progressive playback of a file while the verified engine is still downloading it (Media Source Extensions).
//
// Supported: FRAGMENTED MP4 whose ftyp+moov come first (moov with mvex) and whose tracks are AVC / AAC / MP3 / Opus /
// VP9 that `MediaSource.isTypeSupported` accepts. Everything else → `unsupported`, and the page keeps the normal
// download (the file can still be played/saved once complete).
//
// Guarantees:
//  - only bytes the engine has VERIFIED (per-piece SHA-256) are read (`engine.readVerified`); nothing else reaches the
//    player, and peers only ever get what the engine itself verified;
//  - the file is never assembled in RAM: one fragment at a time (≤ maxSegmentBytes) is read from the piece store;
//  - backpressure: appends stop `maxAheadSec` ahead of the playhead; played-out media older than `keepBehindSec` is
//    evicted from the SourceBuffer; QuotaExceeded evicts and retries once;
//  - the engine is asked to prefer the pieces at the playback position (`engine.prioritize`) — after a seek too;
//  - close() stops everything it started: listeners, waits, the object URL and the media element source.
import { boxHeader, moofTime, parseInit } from './mp4.js';

export const PLAYBACK_LIMITS = Object.freeze({ maxInitBytes: 4 * 1024 * 1024, maxSegmentBytes: 16 * 1024 * 1024, maxAheadSec: 45, keepBehindSec: 30 });
const STOPPED = new Set(['error', 'cancelled', 'expired', 'storage-blocked', 'unsupported']);

class Unsupported extends Error {}

/**
 * @param {{ engine: any, video: HTMLMediaElement, MediaSource?: any, limits?: Partial<typeof PLAYBACK_LIMITS>,
 *           onState?: (state: string, detail?: string) => void }} o
 */
export function createProgressivePlayer({ engine, video, MediaSource: MS = globalThis.MediaSource, limits = {}, onState }) {
  const L = { ...PLAYBACK_LIMITS, ...limits };
  let state = 'idle', detail = null, closed = false;
  let ms = null, sb = null, url = null;
  let segs = [], scanAt = 0, appendIdx = 0, eof = false, ended = false, epoch = 0;
  let timescales = new Map();
  const wakers = new Set();
  const cleanups = [];

  const set = (s, d = null) => { if (state === s && detail === d) return; state = s; detail = d; try { onState?.(s, d); } catch { /* ignore */ } };
  // `pulse` counts wake-ups so a verification that lands between a check and the wait is never lost.
  let pulse = 0;
  const wake = () => { pulse++; for (const w of [...wakers]) w(); };
  const sleepUntilWoken = (since = pulse) => (since !== pulse ? Promise.resolve() : new Promise((r) => { const w = () => { wakers.delete(w); r(); }; wakers.add(w); }));
  const on = (target, ev, fn) => { target.addEventListener(ev, fn); cleanups.push(() => target.removeEventListener(ev, fn)); };
  const guard = () => { if (closed) throw new Closed(); if (STOPPED.has(engine.status)) throw new Error(`transfer ${engine.status}`); };

  /**
   * Verified bytes [off, off+len), waiting (and prioritizing) until the engine has them. A range the engine could
   * never return (outside the file, or larger than one verified read) is refused up front: never an endless wait.
   */
  async function verified(off, len, myEpoch) {
    const size = engine.manifest.sizeBytes;
    if (!Number.isSafeInteger(off) || !Number.isSafeInteger(len) || off < 0 || len < 0 || off + len > size) throw new Unsupported('range outside the file');
    if (len > Math.max(L.maxSegmentBytes, L.maxInitBytes)) throw new Unsupported('range larger than a playback read');
    for (;;) {
      guard();
      if (myEpoch !== undefined && myEpoch !== epoch) throw new Stale();
      const since = pulse;
      const b = await engine.readVerified(off, len);
      if (b) return b;
      engine.prioritize(off);
      await sleepUntilWoken(since);
    }
  }

  function bufferedHas(t) {
    const r = sb ? sb.buffered : null;
    if (r) for (let i = 0; i < r.length; i++) if (t >= r.start(i) && t < r.end(i)) return true;
    return false;
  }

  async function sbDo(fn) {
    if (sb.updating) await new Promise((r) => sb.addEventListener('updateend', r, { once: true }));
    if (closed) throw new Closed();
    await new Promise((resolve, reject) => {
      const done = () => { sb.removeEventListener('updateend', done); sb.removeEventListener('error', bad); resolve(); };
      const bad = () => { sb.removeEventListener('updateend', done); sb.removeEventListener('error', bad); reject(new Error('SourceBuffer error')); };
      sb.addEventListener('updateend', done); sb.addEventListener('error', bad);
      try { fn(); } catch (e) { sb.removeEventListener('updateend', done); sb.removeEventListener('error', bad); reject(e); }
    });
  }

  async function evict() {
    const behind = video.currentTime - L.keepBehindSec;
    const r = sb.buffered;
    if (behind > 0 && r.length && r.start(0) < behind) await sbDo(() => sb.remove(0, behind));
  }

  async function append(bytes) {
    try { await sbDo(() => sb.appendBuffer(bytes)); }
    catch (e) {
      if (!(e && e.name === 'QuotaExceededError')) throw e;
      await evict();
      await sbDo(() => sb.appendBuffer(bytes)); // once; a second failure is an error
    }
  }

  /**
   * Header of the top-level box at `off`, validated BEFORE any of its body is read or awaited: well-formed, at least
   * its own header long, and ending inside the file. Untrusted sizes never turn into an impossible read.
   */
  async function header(off, size) {
    if (size - off < 8) throw new Unsupported('truncated box header');
    const head = await verified(off, Math.min(16, size - off));
    let h;
    try { h = boxHeader(head, 0, size - off); } catch { throw new Unsupported('malformed box size'); }
    if (!h) throw new Unsupported('truncated box header');
    if (h.size > size - off) throw new Unsupported(`${h.type} box extends past the end of the file`);
    return h;
  }

  /** Next top-level box at scanAt; records moof+mdat pairs as segments. */
  async function scanNext(size) {
    const h = await header(scanAt, size);
    if (h.type === 'moof') {
      if (h.size > L.maxSegmentBytes) throw new Unsupported('fragment too large'); // before reading the moof body
      const at = scanAt + h.size;
      const mh = await header(at, size);
      if (mh.type !== 'mdat') throw new Unsupported('moof without mdat');
      const end = at + mh.size;
      if (end - scanAt > L.maxSegmentBytes) throw new Unsupported('fragment too large'); // before reading moof or mdat
      let time;
      try { time = moofTime(await verified(scanAt, h.size), timescales); } catch (e) { if (e instanceof Closed || e instanceof Unsupported) throw e; throw new Unsupported('malformed moof'); }
      segs.push({ start: scanAt, end, time });
      scanAt = end;
    } else if (h.type === 'mfra') { scanAt = size; } // random-access index at the end: nothing to play
    else if (h.type === 'mdat' || h.type === 'moov') throw new Unsupported(`unexpected ${h.type}`);
    else scanAt += h.size; // styp, sidx, prft, emsg, free, skip, …: not needed by the parser
    if (scanAt >= size) eof = true;
  }

  async function loop() {
    const size = engine.manifest.sizeBytes;
    for (;;) {
      guard();
      const myEpoch = epoch, since = pulse;
      try {
        if (appendIdx >= segs.length) {
          if (eof) {
            if (!ended && ms.readyState === 'open' && !sb.updating) { ms.endOfStream(); ended = true; set('complete'); }
            await sleepUntilWoken(since);
            continue;
          }
          await scanNext(size);
          continue;
        }
        const seg = segs[appendIdx], next = segs[appendIdx + 1];
        if (seg.time != null && seg.time > video.currentTime + L.maxAheadSec) { await sleepUntilWoken(since); continue; } // backpressure
        if (seg.time != null && next && next.time != null && bufferedHas(seg.time + 0.01) && bufferedHas(Math.max(seg.time, next.time - 0.05))) { appendIdx++; continue; }
        const bytes = await verified(seg.start, seg.end - seg.start, myEpoch);
        if (myEpoch !== epoch) continue;
        if (ended) { ended = false; } // re-appending after a seek back reopens the stream
        await evict();
        await append(bytes);
        if (myEpoch === epoch) appendIdx++;
        if (state === 'buffering' || state === 'starting') set('playing');
      } catch (e) {
        if (e instanceof Stale) continue;
        throw e;
      }
    }
  }

  function onSeeking() {
    const t = video.currentTime;
    if (bufferedHas(t)) return;
    // Restart appends from the last known fragment at or before t (fragments already buffered are skipped); past the
    // last scanned fragment the scan simply continues forward from there.
    let k = 0;
    for (let i = 0; i < segs.length; i++) if (segs[i].time != null && segs[i].time <= t) k = i;
    epoch++;
    appendIdx = Math.min(k, segs.length);
    engine.prioritize(appendIdx < segs.length ? segs[appendIdx].start : scanAt);
    set('buffering');
    wake();
  }

  async function start() {
    if (state !== 'idle') return state;
    if (!engine.manifest) { set('unsupported', 'no manifest'); return state; }
    if (!MS || typeof MS.isTypeSupported !== 'function') { set('unsupported', 'no Media Source Extensions'); return state; }
    set('starting');
    const unsub = engine.onVerified(() => wake());
    cleanups.push(unsub);
    const unsnap = engine.subscribe(() => wake());
    cleanups.push(unsnap);
    on(video, 'timeupdate', wake);
    on(video, 'seeking', onSeeking);
    on(video, 'error', () => { if (!closed) { set('error', 'media'); close(); } });
    try {
      engine.prioritize(0);
      const size = engine.manifest.sizeBytes;
      // init: every box up to and including moov must come first
      let off = 0, moovEnd = 0;
      while (!moovEnd) {
        if (off >= size || off > L.maxInitBytes) throw new Unsupported('moov not at the start');
        const h = await header(off, size);
        if (h.type === 'mdat' || h.type === 'moof') throw new Unsupported('media before moov');
        if (off + h.size > L.maxInitBytes) throw new Unsupported('init segment too large'); // before reading moov
        off += h.size;
        if (h.type === 'moov') moovEnd = off;
      }
      const init = await verified(0, moovEnd);
      let info;
      try { info = parseInit(init); } catch { throw new Unsupported('malformed moov'); }
      if (!info.fragmented) throw new Unsupported('not a fragmented MP4');
      if (!info.mime || !MS.isTypeSupported(info.mime)) throw new Unsupported(`codec not playable here: ${info.mime || 'unknown'}`);
      timescales = new Map(info.tracks.map((t) => [t.id, t.timescale]));
      ms = new MS();
      url = URL.createObjectURL(ms);
      await new Promise((resolve, reject) => {
        ms.addEventListener('sourceopen', resolve, { once: true });
        const tm = setTimeout(() => reject(new Error('sourceopen timeout')), 10_000);
        cleanups.push(() => clearTimeout(tm));
        video.src = url;
      });
      guard();
      sb = ms.addSourceBuffer(info.mime);
      await append(init);
      scanAt = moovEnd;
      set('buffering', info.mime);
      void loop().catch((e) => {
        if (e instanceof Closed) return;
        set(e instanceof Unsupported ? 'unsupported' : 'error', e && e.message);
        close();
      });
    } catch (e) {
      if (e instanceof Closed) return state;
      set(e instanceof Unsupported ? 'unsupported' : 'error', e && e.message);
      close();
    }
    return state;
  }

  /** Stop playback and release the media element; the caller decides what happens to the transfer. */
  function close() {
    if (closed) return;
    closed = true;
    epoch++;
    for (const c of cleanups.splice(0)) { try { c(); } catch { /* ignore */ } }
    wake(); wakers.clear();
    try { if (sb && ms && ms.readyState === 'open' && sb.updating) sb.abort(); } catch { /* ignore */ }
    try { video.pause(); video.removeAttribute('src'); video.load(); } catch { /* ignore */ }
    if (url) { URL.revokeObjectURL(url); url = null; }
    ms = null; sb = null; segs = [];
    if (state !== 'unsupported' && state !== 'error') set('closed');
  }

  return {
    start, close,
    get state() { return state; },
    get detail() { return detail; },
    /** Debug/test view: fragments discovered and appended so far. */
    get progress() { return { fragments: segs.length, appended: appendIdx, scannedBytes: scanAt, eof }; },
  };
}

class Closed extends Error {}
class Stale extends Error {}
